import { useLazyQuery, useMutation } from "@apollo/client";
import {
  UpdateVendorDetailsInput,
  Vendor,
} from "@towersystems/roam-common/lib/generated-types";
import React from "react";
import { CurrentVendorQuery, UpdateVendorDetailsMutation } from "./constants";
import { VendorControllerContextType } from "./types";
import { CurrentVendorContext, VendorControllerContext } from "./context";

export const useVendorController = (): VendorControllerContextType => {
  const [vendor, _setVendor] = React.useState<Vendor | undefined>(undefined);

  const [queryCurrentVendorFn] = useLazyQuery<{
    currentVendor: Vendor;
  }>(CurrentVendorQuery);

  const [mutationUpdateVendorDetailsFn] = useMutation<
    {
      updateCurrentVendorDetails: Vendor;
    },
    { input: UpdateVendorDetailsInput }
  >(UpdateVendorDetailsMutation);

  const setCurrentVendor = React.useCallback(
    (vendor: Vendor) => {
      _setVendor(vendor);
    },
    [_setVendor]
  );

  const queryCurrentVendor = React.useCallback(async () => {
    return queryCurrentVendorFn().then(async (r) => {
      if (r.data?.currentVendor) {
        setCurrentVendor(r.data.currentVendor);
        return r.data.currentVendor;
      }
      throw new Error();
    });
  }, []);

  const updateVendorDetails = React.useCallback(
    async (input: UpdateVendorDetailsInput) => {
      return mutationUpdateVendorDetailsFn({
        variables: { input },
      }).then(async (r) => {
        if (r.data?.updateCurrentVendorDetails) {
          setCurrentVendor(r.data.updateCurrentVendorDetails);
          return r.data.updateCurrentVendorDetails;
        } else {
          throw new Error("Failed to update vendor details");
        }
      });
    },
    []
  );

  const dangerouslySetCurrentVendor = React.useCallback(
    (updatedVendor: Vendor) => {
      if (!vendor || vendor.id !== updatedVendor.id) {
        throw new Error(
          "Cannot set current vendor to a different vendor than the one already set."
        );
      }
      _setVendor(updatedVendor);
    },
    [_setVendor, vendor]
  );

  const clearCurrentVendor = React.useCallback(async () => {
    _setVendor(undefined);
  }, []);

  return {
    currentVendor: vendor,
    queryCurrentVendor,
    clearCurrentVendor,
    updateVendorDetails,
    dangerouslySetCurrentVendor,
  };
};

export const useVendorControllerProvider = () => {
  const context = React.useContext(VendorControllerContext);
  if (!context) {
    throw new Error("No VendorControllerContext context was provided.");
  }
  return context;
};

export const useCurrentVendorProvider = () => {
  const context = React.useContext(CurrentVendorContext);
  if (!context) {
    throw new Error("No CurrentVendorContext context was provided.");
  }
  return context;
};
